'use client';

import React, { useState } from 'react';

interface AvailabilityCalendarProps {
  bookedDates?: string[];
  onDateSelect?: (checkIn: string, checkOut: string) => void;
  checkIn?: string;
  checkOut?: string;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

function formatDate(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export default function AvailabilityCalendar({ bookedDates = [], onDateSelect, checkIn: externalCheckIn, checkOut: externalCheckOut }: AvailabilityCalendarProps) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectingCheckIn, setSelectingCheckIn] = useState(true);
  const [localCheckIn, setLocalCheckIn] = useState(externalCheckIn || '');
  const [localCheckOut, setLocalCheckOut] = useState(externalCheckOut || '');
  const [hoverDate, setHoverDate] = useState('');

  const checkIn = externalCheckIn !== undefined ? externalCheckIn : localCheckIn;
  const checkOut = externalCheckOut !== undefined ? externalCheckOut : localCheckOut;

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const prevMonth = () => {
    const now = new Date();
    if (viewYear === now.getFullYear() && viewMonth === now.getMonth()) return;
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const handleDayClick = (dateStr: string) => {
    if (bookedDates.includes(dateStr)) return;
    if (new Date(dateStr) < new Date(today.toISOString().split('T')[0])) return;

    if (selectingCheckIn || (checkIn && checkOut)) {
      setLocalCheckIn(dateStr);
      setLocalCheckOut('');
      setSelectingCheckIn(false);
      onDateSelect?.(dateStr, '');
    } else {
      if (dateStr <= checkIn) {
        setLocalCheckIn(dateStr);
        setLocalCheckOut('');
        onDateSelect?.(dateStr, '');
      } else {
        setLocalCheckOut(dateStr);
        setSelectingCheckIn(true);
        onDateSelect?.(checkIn, dateStr);
      }
    }
  };

  const isInRange = (dateStr: string) => {
    if (!checkIn) return false;
    const end = checkOut || hoverDate;
    if (!end || end <= checkIn) return false;
    return dateStr > checkIn && dateStr < end;
  };

  const renderMonth = (year: number, month: number) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const todayStr = today.toISOString().split('T')[0];

    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    return (
      <div className="flex-1">
        <div className="flex items-center justify-between mb-4">
          <button onClick={prevMonth} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors">
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-sm font-bold text-gray-900">{MONTHS[month]} {year}</span>
          <button onClick={nextMonth} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors">
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-7 mb-2">
          {DAYS.map(d => (
            <div key={d} className="text-center text-xs font-semibold text-gray-400 py-1">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {cells.map((day, i) => {
            if (!day) return <div key={`empty-${i}`} />;
            const dateStr = formatDate(year, month, day);
            const isBooked = bookedDates.includes(dateStr);
            const isPast = dateStr < todayStr;
            const isCheckIn = dateStr === checkIn;
            const isCheckOut = dateStr === checkOut;
            const inRange = isInRange(dateStr);
            const isToday = dateStr === todayStr;

            return (
              <div
                key={dateStr}
                className={`relative flex items-center justify-center ${inRange ? 'bg-[#4088C6]/10' : ''} ${isCheckIn && checkOut ? 'rounded-l-full' : ''} ${isCheckOut ? 'rounded-r-full' : ''}`}
              >
                <button
                  onClick={() => handleDayClick(dateStr)}
                  onMouseEnter={() => setHoverDate(dateStr)}
                  onMouseLeave={() => setHoverDate('')}
                  disabled={isBooked || isPast}
                  className={`w-9 h-9 text-sm rounded-full transition-all duration-150 font-medium
                    ${isCheckIn || isCheckOut ? 'bg-gray-900 text-white font-bold' : ''}
                    ${inRange && !isCheckIn && !isCheckOut ? 'text-gray-900' : ''}
                    ${isBooked ? 'text-gray-300 line-through cursor-not-allowed' : ''}
                    ${isPast ? 'text-gray-300 cursor-not-allowed' : ''}
                    ${!isBooked && !isPast && !isCheckIn && !isCheckOut ? 'hover:bg-gray-100 text-gray-700' : ''}
                    ${isToday && !isCheckIn && !isCheckOut ? 'border border-gray-400' : ''}
                  `}
                >
                  {day}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const nextMonthView = viewMonth === 11 ? 0 : viewMonth + 1;
  const nextYearView = viewMonth === 11 ? viewYear + 1 : viewYear;

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Availability</h2>
      <p className="text-sm text-gray-500 mb-6">
        {!checkIn ? 'Select check-in date' : !checkOut ? 'Select check-out date' : `${checkIn} → ${checkOut}`}
      </p>

      <div className="flex flex-col sm:flex-row gap-8">
        {renderMonth(viewYear, viewMonth)}
        <div className="hidden sm:block w-px bg-gray-100" />
        {renderMonth(nextYearView, nextMonthView)}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 mt-6 text-xs text-gray-500">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-gray-900" />
          <span>Selected</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-[#4088C6]/10 border border-[#4088C6]/30" />
          <span>In range</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center">
            <span className="text-gray-300 text-xs line-through">5</span>
          </div>
          <span>Unavailable</span>
        </div>
      </div>
    </div>
  );
}
